import { useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import moment from 'moment'
import { Table } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'
import { RECORD_STATUS } from '@qflow/constants'

import { Queries, useApi } from 'admin/lib/Api'
import { extractPostCodeFromAddress } from 'admin/lib/Functions/postCodeHelper'
import { RecordIcon } from 'admin/lib/Icons/RecordIcon'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'

export function GroupUpdateTable({
  tableProps,
  handleSelectionChange,
  addSpanToWhitespace
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const MAX_RECORD_LINKS = 3
  const KNOWN_SUPPLIER_NAME = 'knownSupplierName'

  const api = useApi()

  // Get list of all Know Companies
  const companies = Queries.useCompanies()
  const allKnownCompanyNames = useMemo(() => {
    const tempAllKnownCompanyNames = {}

    // If fieldName is Known Supplier Name and the company data has loaded
    // Loop through all companies and just add the company name as a new key in the allKnownCompanyNames object
    if (
      tableProps.fieldName === KNOWN_SUPPLIER_NAME &&
      companies.data &&
      companies.data.length > 0
    ) {
      companies.data.forEach(company => {
        tempAllKnownCompanyNames[company.name] = true
      })
    }
    return tempAllKnownCompanyNames
  }, [companies.data, tableProps.fieldName])

  useEffect(() => {
    const handleLoad = async () => {
      return await api.getRecordsFieldValues({
        startDateIso: moment(tableProps.startDate).local().format(),
        endDateIso: moment(tableProps.endDate).local().format(),
        module: tableProps.moduleName,
        accountIds: tableProps.accountIds,
        projectIds: tableProps.projectIds,
        fieldName: tableProps.fieldName,
        hint: tableProps.hint,
        statuses: [RECORD_STATUS.PUBLISHED, RECORD_STATUS.AUTOPUBLISHED]
      })
    }

    setIsLoading(true)
    handleLoad()
      .then(value => {
        if (value[0] && value[0].recordIdsByFieldValue) {
          setData(value[0].recordIdsByFieldValue)
        }

        setIsLoading(false)
      })
      .catch(() => {
        setData({})
      })
    setSelectedRowKeys([])
  }, [api, tableProps])

  const NULL_REPLACEMENT = '-blank-'

  const formatTableData = () => {
    const formattedData = []

    if (data && Object.keys(data).length > 0) {
      for (const [key, value] of Object.entries(data)) {
        const keyObject = {
          value: key,
          recordAndProjectIds: value.map(record => {
            return { recordId: record.recordId, projectId: record.projectId }
          }),
          count: value.length
        }

        formattedData.push({
          key: key !== 'null' ? JSON.stringify(keyObject) : NULL_REPLACEMENT,
          value: key !== 'null' ? key : NULL_REPLACEMENT,
          recordAndProjectIds: value,
          count: value.length
        })
      }
    }
    return formattedData
  }

  const highlightUnknownSupplierName = value => {
    if (Object.prototype.hasOwnProperty.call(allKnownCompanyNames, value)) {
      return addSpanToWhitespace(value)
    } else {
      return (
        <NotMatched title="Value not found in Companies list">
          {addSpanToWhitespace(value)}
        </NotMatched>
      )
    }
  }

  const columns = [
    {
      title: 'Value',
      dataIndex: 'value',
      sorter: (a, b) => {
        if (a.value === NULL_REPLACEMENT || b.value === NULL_REPLACEMENT) {
          return a.value === b.value ? 0 : a.value === NULL_REPLACEMENT ? -1 : 1
        }
        return a.value.localeCompare(b.value)
      },
      render: value =>
        tableProps.fieldName === KNOWN_SUPPLIER_NAME
          ? highlightUnknownSupplierName(value)
          : addSpanToWhitespace(value),
      width: '70%'
    },
    {
      title: 'Count',
      dataIndex: 'count',
      sorter: (a, b) => a.count - b.count,
      width: '10%',
      align: 'center'
    },
    {
      title: `Related records (max ${MAX_RECORD_LINKS})`,
      dataIndex: 'recordAndProjectIds',
      width: '15%',
      align: 'center',
      render: valueArray => {
        const links = []
        for (let i = 0; i < MAX_RECORD_LINKS && i < valueArray.length; i++) {
          links.push(
            <EditLink
              key={`${valueArray[i].accountId}-${valueArray[i].projectId}-${valueArray[i].recordId}`}
              href={generatePath(ROUTES.RECORD.EDIT, {
                accountId: valueArray[i].accountId,
                projectId: valueArray[i].projectId,
                moduleName: tableProps.moduleName,
                recordId: valueArray[i].recordId
              })}
            >
              <RecordIcon />
            </EditLink>
          )
        }
        return links
      }
    }
  ]

  // If tableProps.fieldName is an address field add an extra column
  if (tableProps.fieldName.includes('Address')) {
    const postCodeColumn = {
      title: 'Post code',
      dataIndex: 'value',
      sorter: (a, b) => {
        if (a.value === NULL_REPLACEMENT || b.value === NULL_REPLACEMENT) {
          return a.value === b.value ? 0 : a.value === NULL_REPLACEMENT ? -1 : 1
        }
        return extractPostCodeFromAddress(a.value).localeCompare(
          extractPostCodeFromAddress(b.value)
        )
      },
      render: value => addSpanToWhitespace(extractPostCodeFromAddress(value)),
      width: '10%'
    }
    columns.splice(1, 0, postCodeColumn)
  }

  const sortedColumns = () => {
    let customSort = null
    try {
      const storedSort = localStorage.getItem('customSort')
      customSort = JSON.parse(storedSort)
    } catch (error) {
      console.error('Error parsing JSON:', error)
    }
    const sortingColumn = customSort?.field ?? 'value'
    const sortingDirection = customSort?.order ?? 'ascend'

    const targetColumn = columns.find(
      column => column.dataIndex === sortingColumn
    )
    targetColumn.defaultSortOrder = sortingDirection

    return columns
  }

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys)
    handleSelectionChange(newSelectedRowKeys.map(key => JSON.parse(key)))
  }

  const rowSelection = {
    selectedRowKeys,
    columnWidth: '48px',
    onChange: onSelectChange,
    getCheckboxProps: record => {
      if (NULL_REPLACEMENT === record.value)
        return {
          disabled: true
        }
    }
  }

  const onChange = (pagination, filters, sorter, extra) => {
    localStorage.setItem('customSort', JSON.stringify(sorter))
  }

  if (isLoading) {
    return <ScreenSpin id="userLoadingSpinner" />
  }

  return (
    <Table
      columns={sortedColumns()}
      dataSource={formatTableData()}
      pagination={false}
      rowSelection={rowSelection}
      scroll={{ y: 'calc(100vh - 400px)' }}
      sticky
      onChange={onChange}
    />
  )
}

const EditLink = styled.a`
  display: inline-block;
  height: 16px;
  max-height: 16px;
  margin-right: 8px;
  border-bottom: 0;
  vertical-align: text-top;

  :visited {
    color: ${colours.DEEP_SKY_BLUE};
  }

  :focus {
    border-bottom: 1px solid;
  }

  :hover {
    border-bottom: 0;
    background: ${colours.PALE_GREY};
  }
`
const NotMatched = styled.span`
  color: #ff0000;
`
